import { IObjectStringKeyMap } from '@shared/models';
import { ObservationHistory } from '@services/observations/observation.interfaces';
import { ContentPickerSelection } from '@modules/management/pages/details/content/components';
import { FolderPickerSelection } from '@modules/management/modules/folders/model/folders.interfaces';
import {
  IPropertyEventActivity,
  IPropertyEventValue,
  IPropertyMeta
} from './../property/property-model.interfaces';
import {
  AssetStateHistoryEntity
} from '@modules/observation/pages/asset-status/pages/detail/model/asset-state.interfaces';
import {
  IExternalSourceSelectedItem
} from '@modules/management/modules/external-sources-picker/model/external-sources-picker.interfaces';

export interface Asset {
  active: number;
  assetID: number;
  assetType: number;
  assetUUID: string;
  beacons: string[];
  contentItems: string[];
  created: number;
  creator: number;
  disabledAt: number;
  disabledBy: number;
  folderID: number;
  identifier: string;
  images: string[];
  monitorHealth: number;
  health: number;
  stateEntered?: number;
  lastUpdate: number;
  location: number;
  name: string;
  nfcTags: string[];
  translations: string[];
  users: [string];
  parentContent?: ContentPickerSelection[];
  properties?: IPropertyMeta[];
  parentProperties?: IPropertyMeta[];
  selectedProperties?: IPropertyMeta[];
  sources?: number[];
  parentSources?: IExternalSourceSelectedItem[];
  selectedSources?: IExternalSourceSelectedItem[];
  observationEvents?: ObservationHistory[];
  propertyValues?: IObjectStringKeyMap<IAssetPropertyValue>;
  propertyEvents?: IObjectStringKeyMap<IAssetPropertyValue[]>;
  zone: number;
  currentZone: number;
  healthEvents?: IObjectStringKeyMap<HealthEvent[]>;
  stateEvents?: IObjectStringKeyMap<StateEvent[]>;
  lastEvent?: number;
  // CVX-5984: per-asset cursor fields for incremental fetch
  maxPropertyEventID?: number;
  maxHealthEventID?: number;
}

export interface ReportAsset extends Asset {
  healthEntities?: AssetStateHistoryEntity[];
  stateEntities?: AssetStateHistoryEntity[];  // asset:inUse state history (CVX-6485)
}

export interface HealthEvent {
  id?: number; // CVX-5984: server-assigned row id, used as incremental cursor dedup key
  health: number;
  metric: number;
  time: number;
  workTimeSeconds?: number;
  clockTimeSeconds?: number;
}

/** One change in an asset:inUse property's state (getAssets stateEvents, newest first). */
export interface StateEvent {
  id: number;
  time: number;
  state: 0 | 1;
  activity: string;
  workTimeSeconds: number;  // time spent in the previous state
  clockTimeSeconds: number;
}

export interface IAssetPropertyValue {
  id?: number; // CVX-5984: server-assigned row id, used as incremental cursor dedup key
  activity: PropertyActivity | IPropertyEventActivity;
  time: number;
  userID: number;
  value: string | IPropertyValueZone | IPropertyEventValue;
  health?: number;
  source?: string;
  workTimeSeconds?: number;
  clockTimeSeconds?: number;
}

export interface IPropertyValueZone {
  rssi: number;
  zoneID: number;
}

export interface AssetFilterParameters {
  locations?: number[];
  zones?: (number | string)[];
  users?: number[];
  states?: number[];
  assetsFolders?: FolderPickerSelection[];
}

export enum AssetFilterMenuFormField {
  AssetFolderPicker = 'assetFolderPicker',
  AssetStates = 'assetStates'
}

export enum PropertyActivity {
  OutOfRange = 'outOfRange',
  InRange = 'inRange',
  Manual = 'manual',
  Calculated = 'calculated',
}

// CVX-5984: per-(assetID, windowKey) incremental fetch cache entry
export interface AssetEventsCache {
  propertyEvents: IObjectStringKeyMap<IAssetPropertyValue[]>;
  healthEvents: IObjectStringKeyMap<HealthEvent[]>;
  stateEvents?: IObjectStringKeyMap<StateEvent[]>;
  propertyValues: IObjectStringKeyMap<IAssetPropertyValue>;
  lastPropertyCursor: number;  // maxPropertyEventID from last server response (integer row ID)
  lastHealthCursor: number;    // maxHealthEventID from last server response (integer row ID)
  windowKey: string;
}
